import React, { useEffect, useRef, useState } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import { fetchPostTruelayerBankAuthenticationUrl } from '@iwoca/lapi-client/edge';
import {
    AlertBox,
    Button,
    Icon,
    Input,
    IwocaLogo,
    Link,
    Modal
} from '@iwoca/orion';
import cn from 'classnames';
import {
    FormattedMessage,
    WrappedComponentProps,
    injectIntl
} from 'react-intl';
import { connect } from 'react-redux';

import { getAmplitudeDefaultEventProperties } from '@src/tracking/amplitudeEvents';

import messages from './TrueLayerModal.messages';
import styles from './TrueLayerModal.module.css';
import {
    getCountryCode,
    getStateKey
} from '../../../../lendingAPI/lendingAPISelector';
import { RootState } from '../../../../reducers';
import BankLinksSVG from '../../../../svgComponents/BankLinkSVG/BankLinkSVG';

export type TTrueLayerBank = {
    bank_id: string;
    bank_name: string;
    logo_url: string;
};

const mapStateToProps = (state: RootState) => ({
    country: getCountryCode(state),
    stateKey: getStateKey(state)
});

type propsFromRedux = ReturnType<typeof mapStateToProps>;

enum trueLayerModal {
    NONE = 'none',
    BANK_LIST = 'bankList',
    BANK_CONNECTION = 'bankConnection'
}

const TrueLayerModal = ({
    intl,
    isOpen,
    handleClose,
    bankList,
    stateKey,
    country
}: {
    isOpen: boolean;
    handleClose: () => void;
    bankList: TTrueLayerBank[];
} & propsFromRedux &
    WrappedComponentProps) => {
    const [banks, setBanks] = useState<TTrueLayerBank[]>(bankList);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedBank, setSelectedBank] = useState<TTrueLayerBank | null>(
        null
    );
    const [view, setView] = useState<trueLayerModal>(trueLayerModal.BANK_LIST);
    const [trueLayerBankUrl, setTrueLayerBankUrl] = useState('');
    const [bankUrlError, setBankUrlError] = useState(false);
    const bankHeaderRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        bankHeaderRef.current?.scrollIntoView({ block: 'center' });
    }, [view]);

    const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setSearchTerm(value);

        if (value === '') return setBanks(bankList);

        const filteredBanks = bankList.filter((bank) =>
            bank.bank_name.toLowerCase().includes(value.toLowerCase())
        );

        setBanks(filteredBanks);
    };

    const handleSelectedBank = async (bank: TTrueLayerBank) => {
        tracking.ampli.trueLayerBankSelection({
            bankId: bank.bank_id,
            bankName: bank.bank_name
        });
        setSelectedBank(bank);
        setView(trueLayerModal.BANK_CONNECTION);

        try {
            const response = await fetchPostTruelayerBankAuthenticationUrl({
                stateKey: stateKey,
                providerId: bank.bank_id
            });

            if (response) {
                setTrueLayerBankUrl(response?.data?.bank_url);
            }
        } catch {
            setBankUrlError(true);
        }
    };

    const handleBackButton = () => {
        setTrueLayerBankUrl('');
        setBankUrlError(false);
        setView(trueLayerModal.BANK_LIST);
    };

    const handleBankConnectionTracking = () => {
        tracking.ampli.trueLayerBankConnectStarted({
            ...getAmplitudeDefaultEventProperties(),
            bankId: selectedBank?.bank_id,
            bankName: selectedBank?.bank_name
        });
    };

    const renderErrorBankList = () => {
        return (
            <p className={styles.bankListError}>
                <FormattedMessage {...messages.bankListError} />
            </p>
        );
    };

    const renderBankGradeEncryption = () => {
        return (
            <div className={styles.bankGradeContainer}>
                <Icon icon="shieldCheck" className={styles.bankGradeIcon} />
                <span>
                    <FormattedMessage {...messages.bankEncryption} />
                </span>
            </div>
        );
    };
    const renderBankListView = () => {
        return (
            <>
                <div className={styles.bankListHeader}>
                    <h3 className={styles.bankListTitle}>
                        <FormattedMessage {...messages.title} />
                    </h3>
                    {renderBankGradeEncryption()}
                </div>
                <p className={styles.bankListDescription}>
                    <FormattedMessage {...messages.description} />
                </p>
                <Input
                    iconLeft="zoomOutline"
                    className={styles.searchBar}
                    placeholder={intl.formatMessage(
                        messages.searchBarPlaceholder
                    )}
                    onChange={handleSearch}
                    value={searchTerm}
                />

                <div
                    className={cn(styles.banksGridContainer, {
                        [styles.bankContainerError]: banks.length < 1
                    })}>
                    {banks &&
                        banks.map((bank: TTrueLayerBank) => {
                            return (
                                <figure key={bank.bank_id}>
                                    <div
                                        className={styles.bankContainer}
                                        onClick={() =>
                                            handleSelectedBank(bank)
                                        }>
                                        <img
                                            src={bank.logo_url}
                                            alt={bank.bank_name}
                                            className={styles.bankLogo}
                                        />
                                        <figcaption className={styles.bankName}>
                                            {bank.bank_name}
                                        </figcaption>
                                    </div>
                                </figure>
                            );
                        })}
                    {banks.length < 1 && renderErrorBankList()}
                </div>
                {country === 'uk' && (
                    <div
                        className={styles.trueLayerFooter}
                        data-testid="true-layer-modal-footer">
                        <p>
                            <small>
                                <FormattedMessage
                                    {...messages.bankListFooter}
                                    values={{
                                        footerLinkText: (
                                            <Link
                                                data-testid="fca-regulated-link"
                                                href={intl.formatMessage(
                                                    messages.bankListFooterLink
                                                )}
                                                target="_blank"
                                                rel="noopener noreferrer">
                                                <FormattedMessage
                                                    {...messages.bankListFooterLinkText}
                                                />
                                            </Link>
                                        )
                                    }}
                                />
                            </small>
                        </p>
                    </div>
                )}
            </>
        );
    };

    const renderBankConnectionView = () => {
        return (
            <>
                <div
                    ref={bankHeaderRef}
                    className={styles.bankConnectionHeader}>
                    <Button
                        data-testid="bank-connection-back"
                        variant="tertiary"
                        onClick={handleBackButton}
                        className={styles.backButton}
                        iconOnly>
                        <Icon icon="angleLeft" />
                    </Button>
                    <div className={styles.bankLogosContainer}>
                        <div className={styles.bankConnectionLogos}>
                            <IwocaLogo iconOnly className={styles.iwocaLogo} />
                            <BankLinksSVG classNames={styles.bankLink} />
                            <figure>
                                <img
                                    src={selectedBank?.logo_url}
                                    alt={selectedBank?.bank_name}
                                    className={styles.bankLogo}
                                />
                            </figure>
                        </div>
                    </div>
                </div>
                <section className={styles.bankConsentContainer}>
                    <h3>
                        <strong>
                            <FormattedMessage
                                {...messages.bankConnectionTitle}
                            />
                        </strong>
                    </h3>
                    <p>
                        <FormattedMessage
                            {...messages.bankConnectionDescription}
                        />
                    </p>
                    <ul>
                        <li>
                            <strong>
                                <FormattedMessage
                                    {...messages.sharedInfoFullName}
                                />
                            </strong>
                        </li>
                        <li>
                            <strong>
                                <FormattedMessage
                                    {...messages.sharedInfoAccountNumber}
                                />
                            </strong>
                        </li>
                        <li>
                            <strong>
                                <FormattedMessage
                                    {...messages.sharedInfoAccountBalance}
                                />
                            </strong>
                        </li>
                        <li>
                            <strong>
                                <FormattedMessage
                                    {...messages.sharedInfoTransactionHistory}
                                />
                            </strong>
                        </li>
                    </ul>
                    <div className={styles.bankConnectionGradeContainer}>
                        {renderBankGradeEncryption()}
                        <p>
                            <FormattedMessage
                                {...messages.bankEncryptionIntroText}
                            />
                        </p>
                        <p>
                            <FormattedMessage
                                {...messages.bankEncryptionPermissionText}
                            />
                        </p>
                        <p>
                            <FormattedMessage
                                {...messages.bankEncryptionRegulatedText}
                            />
                        </p>
                    </div>
                    <div
                        className={styles.trueLayerFooter}
                        data-testid="true-layer-modal-footer">
                        <p>
                            <small>
                                <FormattedMessage
                                    {...messages.bankConnectionFooter}
                                    values={{
                                        termsOfServiceLinkText: (
                                            <Link
                                                data-testid="terms-of-service-link"
                                                href={intl.formatMessage(
                                                    messages.bankConnectionFooterTermsLink
                                                )}
                                                target="_blank"
                                                rel="noopener noreferrer">
                                                <FormattedMessage
                                                    {...messages.bankConnectionFooterTermsLinkText}
                                                />
                                            </Link>
                                        ),
                                        privacyPolicyLinkText: (
                                            <Link
                                                data-testid="privacy-policy-link"
                                                href={intl.formatMessage(
                                                    messages.bankConnectionFooterPrivacyLink
                                                )}
                                                target="_blank"
                                                rel="noopener noreferrer">
                                                <FormattedMessage
                                                    {...messages.bankConnectionFooterPrivacyLinkText}
                                                />
                                            </Link>
                                        )
                                    }}
                                />
                            </small>
                        </p>
                    </div>
                </section>
                {bankUrlError && selectedBank && (
                    <AlertBox variant="failure">
                        <p className={styles.bankUrlError}>
                            <FormattedMessage
                                {...messages.bankConnectionError}
                                values={{ bankName: selectedBank.bank_name }}
                            />
                        </p>
                    </AlertBox>
                )}
                <div className={styles.ctaContainer}>
                    <Button
                        data-testid="truelayer-bank-url"
                        href={trueLayerBankUrl}
                        onClick={handleBankConnectionTracking}
                        rel="noopener noreferrer"
                        loading={!trueLayerBankUrl && !bankUrlError}
                        disabled={bankUrlError}
                        aria-disabled={bankUrlError}
                        size="large">
                        <FormattedMessage
                            {...messages.bankConnectionAllowCTA}
                        />
                    </Button>
                    <Button
                        variant="tertiary"
                        onClick={handleClose}
                        size="large">
                        <FormattedMessage
                            {...messages.bankConnectionCancelCTA}
                        />
                    </Button>
                </div>
            </>
        );
    };

    return (
        // TODO: Orion modal is flushed to the border on mobile. Need to fix that to show some margin.
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            closeIcon={true}
            headerBar={true}
            title={intl.formatMessage(messages.modalTitle)}
            className={styles.modalContainer}>
            {view === trueLayerModal.BANK_LIST && renderBankListView()}
            {view === trueLayerModal.BANK_CONNECTION &&
                renderBankConnectionView()}
        </Modal>
    );
};

const connectorHOC = connect(mapStateToProps);

export default connectorHOC(injectIntl(TrueLayerModal));
